import type {
  ExperienceList,
  LinkCreate,
  LinkList,
  LinkSummary,
  LinkUpdate,
} from '@matter_extractor/shared';
import { experienceListSchema, linkListSchema, linkSummarySchema } from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The calls of Réglages > Partage (§5.1, §11.4) - the links, and the one read
 * of the experiences their creation form needs.
 *
 * ## Why a file of its own rather than an eighth family in `lib/api.ts`
 *
 * `api.ts` is being rewritten by the Expériences lot in the same cycle, and two
 * lots editing one four-hundred-line module is a merge that goes wrong in the
 * only place it cannot be seen - a request that silently keeps the other
 * branch's shape. The families of `api.ts` are already separate by object; this
 * is one more, with its own file. `ApiError` is IMPORTED from there rather than
 * re-declared: it is the type every screen catches, and two classes of the same
 * name would make `instanceof` answer false on one of them.
 *
 * The `request` helper below is the one deliberate duplication, and it is
 * fifteen lines: `api.ts` does not export its own, and exporting it would have
 * been an edit to the file this separation exists to avoid. The behaviour it
 * has to keep is exactly one thing - `details` falls back to the WHOLE body -
 * because a business refusal answers the contract body with no `details` key at
 * all (`versioning/app.ts`, and `routes/links.ts` beside it), and reading only
 * `body.details` made every typed refusal of §4 arrive as `undefined` (found in
 * issue #41).
 *
 * Every answer is PARSED before it is returned: a response is an external input
 * even when we wrote the server.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/**
 * The credential travels in a cookie and NEVER in the URL - the line the whole
 * administration holds. Same object as `ADMIN_INIT` of `api.ts`, and `as const
 * satisfies` for the same reason: `credentials` has to stay the literal type.
 */
const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, { ...ADMIN_INIT, ...init });
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      // The fallback to the whole body is what carries a business refusal out:
      // `c.json(err.refusal, 409)` has no `details` key - see the head of this
      // file.
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

/* ---------------------------------------------------------------- the links */

/** The whole list, revoked and expired included - the page greys them (§11.4). */
export function fetchLinks(): Promise<LinkList> {
  return request('/api/links').then((body) => linkListSchema.parse(body));
}

/** « Créer le lien ». The secret comes BACK; it is never sent (§5.1). */
export function createLink(body: LinkCreate): Promise<LinkSummary> {
  return request('/api/links', { method: 'POST', body: JSON.stringify(body) }).then((answer) =>
    linkSummarySchema.parse(answer),
  );
}

/** « Modifier »: the name, the lifetime and the entries, together. */
export function updateLink(id: string, body: LinkUpdate): Promise<LinkSummary> {
  return request(`/api/links/${id}`, { method: 'PUT', body: JSON.stringify(body) }).then((answer) =>
    linkSummarySchema.parse(answer),
  );
}

/** « Prolonger »: the lifetime restarts from today. No body - the gesture IS the request. */
export function extendLink(id: string): Promise<LinkSummary> {
  return request(`/api/links/${id}/extension`, { method: 'POST' }).then((answer) =>
    linkSummarySchema.parse(answer),
  );
}

/** « Révoquer ». Idempotent server side, so a double click costs nothing. */
export function revokeLink(id: string): Promise<LinkSummary> {
  return request(`/api/links/${id}/revocation`, { method: 'POST' }).then((answer) =>
    linkSummarySchema.parse(answer),
  );
}

/* ---------------------------------------------------------- the experiences */

/**
 * The library, for the select of an entry row (§11.4).
 *
 * The SAME route the Expériences lot reads, and deliberately not a narrower one
 * of its own: §4 serves each library whole and the page filters it, so a
 * dedicated « experiences for a select » endpoint would be a second shape of
 * one list. The card carries everything a row needs - the name, the production
 * pointer, the retirement - which is what lets the form grey « Créer le lien »
 * without a round trip.
 */
export function fetchExperiencesForLinks(): Promise<ExperienceList> {
  return request('/api/experiences').then((body) => experienceListSchema.parse(body));
}
